import { IconButton, Popover, TextField, Typography } from "@mui/material";
import Close from "@mui/icons-material/Close";
import CalendarToday from "@mui/icons-material/CalendarToday";
import MoreHoriz from "@mui/icons-material/MoreHoriz";
import { DragEvent, MouseEvent, useRef, useState } from "react";
import { emptyRepeat, TaskLink } from "../../../api/Tasks";
import { TaskCheckbox } from "../TaskCheckbox";
import moment from "moment";
import { dateFormat, shortDate } from "../../../misc/dates";
import { TaskDueDate } from "../TaskDueDate";
import { CopyLinkButton } from "../CopyLinkButton";
import { DueDatePanel } from "./DueDatePanel";
import { panelFontSize, PanelRow } from "./PanelSection";

// TaskLinkRow is one task in a panel list: tick it off, read or rename its title, and
// open the controls sitting at the right-hand end.
export function TaskLinkRow(props: {
    value: TaskLink;
    busy: boolean;

    onToggle: (completed: boolean) => void;
    onRemove: () => void;
    onContextMenu: (e: MouseEvent) => void;

    // onMenu opens the same menu from the row's "..." button, at the point given
    onMenu: (point: { x: number; y: number }) => void;

    // onHold opens the same menu from a touch and hold
    onHold?: (point: { x: number; y: number }) => void;

    // removeLabel names what the close button does, since a section either takes an
    // item out of a list or ends the task's membership of one
    removeLabel?: string;

    // onOpen puts the task behind its title, so clicking the title opens it. A row
    // that renames in place goes without.
    onOpen?: () => void;

    // onRename turns the title into a field that is edited in place
    onRename?: (title: string) => void;

    // onEnter runs after the title field is saved with the Enter key
    onEnter?: () => void;

    // onSchedule puts a calendar button on the row, which is where the date is set. It
    // takes the whole date rather than the part that changed, so that a save landing
    // out of order can't leave the two fields disagreeing. "" is no date.
    onSchedule?: (date: { dueDate: string; dueTime: string }) => void;

    // compact draws the due date at caption size and drops the right gutter, for a
    // long list where the title leads
    compact?: boolean;

    // completedDate shows the day the task was completed in place of its due date
    completedDate?: boolean;

    // expectedDate is the day the task is expected to be ticked off. A task ticked off
    // on another day shows that day.
    expectedDate?: string | null;

    draggable?: boolean;
    onDragStart?: (e: DragEvent) => void;
    onDragEnd?: () => void;
    onDragOver?: (e: DragEvent) => void;
    onDrop?: (e: DragEvent) => void;
}) {
    const link = props.value;

    // the title being edited, or null while it's only being read
    const [title, setTitle] = useState<string | null>(null);

    const dateAnchor = useRef<HTMLButtonElement>(null);
    const [dateOpen, setDateOpen] = useState(false);

    // the date panel reads from what was picked rather than from the row, so it keeps
    // up with the pointer instead of with the server
    const [date, setDate] = useState({ dueDate: "", dueTime: "" });

    const openDate = () => {
        setDate({ dueDate: link.dueDate || "", dueTime: link.dueTime || "" });
        setDateOpen(true);
    };

    const changeDate = (changes: { dueDate?: string; dueTime?: string }) => {
        const next = { ...date, ...changes };
        setDate(next);
        if (props.onSchedule) props.onSchedule(next);
    };

    if (link.noAccess) {
        return (
            <PanelRow onContextMenu={e => e.preventDefault()}>
                <Typography variant="body2" noWrap color="text.disabled" sx={{ flex: 1, minWidth: 0, pl: 0.5, fontSize: panelFontSize }}>
                    No access to task #{link.id}
                </Typography>
                <IconButton
                    className="panel-row-action"
                    size="small"
                    title={props.removeLabel || "Remove"}
                    sx={{ opacity: 0 }}
                    disabled={props.busy}
                    onClick={() => props.onRemove()}
                >
                    <Close fontSize="inherit" />
                </IconButton>
            </PanelRow>
        );
    }

    const doneOn = link.completedAt ? moment(link.completedAt).format(dateFormat) : null;
    const offDay = !!props.expectedDate && !!doneOn && doneOn !== props.expectedDate;

    const commit = () => {
        const value = (title || "").trim();
        setTitle(null);
        if (!value || value === link.title || !props.onRename) return;

        props.onRename(value);
    };

    return (
        <PanelRow
            onContextMenu={props.onContextMenu}
            // a hold inside the title field is left to select its text
            onHold={title === null ? props.onHold : undefined}
            compact={props.compact}
            // a drag would take over selecting text in the title field
            draggable={props.draggable && title === null}
            onDragStart={props.onDragStart}
            onDragEnd={props.onDragEnd}
            onDragOver={props.onDragOver}
            onDrop={props.onDrop}
        >
            <TaskCheckbox
                dense
                checked={!!link.completedAt}
                disabled={props.busy}
                onChange={checked => props.onToggle(checked)}
            />


            {title !== null ? (
                <TextField
                    variant="standard"
                    autoFocus
                    fullWidth
                    sx={{ flex: 1, minWidth: 0 }}
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    onBlur={commit}
                    onKeyDown={e => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            commit();
                            if (props.onEnter) props.onEnter();
                            return;
                        }

                        if (e.key === "Escape") {
                            e.preventDefault();
                            setTitle(null);
                        }
                    }}
                    slotProps={{
                        input: { disableUnderline: true, style: { fontSize: panelFontSize } },
                    }}
                />
            ) : (
                <Typography
                    variant="body2"
                    noWrap
                    onClick={() => props.onRename ? setTitle(link.title) : props.onOpen && props.onOpen()}
                    sx={{
                        flex: 1, minWidth: 0, fontSize: panelFontSize,
                        cursor: props.onRename ? "text" : props.onOpen ? "pointer" : undefined,
                        color: link.completedAt ? "text.secondary" : undefined,
                        textDecoration: link.completedAt ? "line-through" : undefined,
                        "&:hover": props.onRename ? undefined : {
                            textDecoration: link.completedAt ? "line-through" : "underline",
                        },
                    }}
                >
                    {link.title}
                </Typography>
            )}

            {props.completedDate ? (
                link.completedAt && (
                    <Typography variant={props.compact ? "caption" : "body2"} color="text.secondary"
                                noWrap sx={{ flexShrink: 0 }}>
                        {shortDate(moment(link.completedAt).format(dateFormat))}
                    </Typography>
                )
            ) : <TaskDueDate dueDate={link.dueDate} dueTime={link.dueTime} small={props.compact}
                         completed={!!link.completedAt} />}

            {offDay && !props.completedDate && (
                <Typography variant={props.compact ? "caption" : "body2"} color="text.secondary"
                            noWrap sx={{ flexShrink: 0 }} title="Completed">
                    {moment(doneOn, dateFormat).format("MMM-DD")}
                </Typography>
            )}

            {props.onSchedule && (
                <>
                    <IconButton
                        ref={dateAnchor}
                        className="panel-row-action"
                        size="small"
                        title="Due date"
                        // the button stays up while its panel is open, so it doesn't
                        // vanish from under the pointer
                        sx={{ opacity: dateOpen ? 1 : 0 }}
                        disabled={props.busy}
                        onClick={openDate}
                    >
                        <CalendarToday fontSize="inherit" />
                    </IconButton>

                    <Popover
                        open={dateOpen}
                        anchorEl={dateAnchor.current}
                        onClose={() => setDateOpen(false)}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                    >
                        <DueDatePanel
                            hideRepeat
                            dueDate={date.dueDate}
                            dueTime={date.dueTime}
                            repeat={emptyRepeat}
                            onChange={changeDate}
                        />
                    </Popover>
                </>
            )}

            <CopyLinkButton task={link} className="panel-row-action panel-row-hover-only" opacity={0} />

            <IconButton
                className="panel-row-action"
                size="small"
                title="More"
                sx={{ opacity: 0 }}
                disabled={props.busy}
                // the menu drops from under the button, so a keyboard press opens it there too
                onClick={e => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    props.onMenu({ x: rect.left, y: rect.bottom });
                }}
            >
                <MoreHoriz fontSize="inherit" />
            </IconButton>

            <IconButton
                className="panel-row-action panel-row-hover-only"
                size="small"
                title={props.removeLabel || "Remove"}
                sx={{ opacity: 0 }}
                disabled={props.busy}
                onClick={() => props.onRemove()}
            >
                <Close fontSize="inherit" />
            </IconButton>
        </PanelRow>
    );
}
